<script setup>
import TireVaultLogo from '@/components/Brand/TireVaultLogo';
import { useUserStore } from '@/stores/user';
import { loadLanguageAsync } from 'laravel-vue-i18n';
import { ref, watch } from 'vue';

const { setLocale } = useUserStore();
const language = ref('en');

watch(language, (lang) => {
    loadLanguageAsync(lang);
    setLocale(lang);
});
</script>
<template>
<div class="min-h-screen flex flex-col md:flex-row bg-surface">
    <div class="relative md:w-[45%] lg:w-[42%] flex flex-col justify-between p-8 md:p-10 shrink-0 overflow-hidden h-44 md:h-auto bg-primary">
        <svg class="absolute inset-0 w-full h-full" aria-hidden="true">
            <defs>
                <pattern
                    id="tread-lanes"
                    width="200"
                    height="200"
                    patternUnits="userSpaceOnUse">
                    <g
                        transform="translate(70,72) scale(1.05)"
                        stroke="rgba(255,255,255,0.045)"
                        fill="rgba(255,255,255,0.045)">
                        <circle
                            cx="30"
                            cy="22"
                            r="17"
                            stroke-width="7"
                            fill="none" />
                        <circle
                            cx="30"
                            cy="22"
                            r="4.5"
                            stroke="none" />
                        <line
                            x1="10"
                            y1="43"
                            x2="50"
                            y2="43"
                            stroke-width="4"
                            stroke-linecap="round" />
                        <line
                            x1="2"
                            y1="16"
                            x2="8"
                            y2="16"
                            stroke-width="4"
                            stroke-linecap="round" />
                        <line
                            x1="0"
                            y1="26"
                            x2="7"
                            y2="26"
                            stroke-width="4"
                            stroke-linecap="round" />
                    </g>
                </pattern>
            </defs>
            <rect
                width="100%"
                height="100%"
                fill="url(#tread-lanes)" />
        </svg>
        <div
            class="absolute inset-0"
            style="background: linear-gradient(160deg, rgba(11,44,70,0.35) 0%, transparent 45%, rgba(11,44,70,0.45) 100%);"
            aria-hidden="true" />

        <div class="relative">
            <TireVaultLogo :size="30" class="text-xl" />
        </div>

        <div class="relative hidden md:block">
            <p class="font-display font-bold text-white text-3xl leading-snug mb-3">
                {{ $t('auth.login.tagline_line_1') }}<br>{{ $t('auth.login.tagline_line_2') }}<br>{{ $t('auth.login.tagline_line_3') }}
            </p>
            <p class="text-white/60 text-sm max-w-xs">{{ $t('auth.login.tagline_sub') }}</p>
        </div>

        <p class="relative hidden md:block font-mono text-[11px] text-white/40">{{ $t('auth.login.dealership_access_only') }}</p>
    </div>

    <div class="flex-1 flex items-center justify-center p-8 md:p-10">
        <div class="w-full max-w-sm">
            <h1 class="font-display font-bold text-2xl mb-1 text-ink">
                <slot name="title" />
            </h1>
            <p
                v-if="$slots.subtitle"
                class="text-sm mb-8 text-ink-light">
                <slot name="subtitle" />
            </p>

            <slot />

            <div class="flex justify-center mt-10">
                <v-btn-toggle
                    v-model="language"
                    density="compact"
                    variant="text"
                    color="primary"
                    mandatory
                    group>
                    <v-btn
                        value="en"
                        size="small">{{ $t('common.en') }}</v-btn>
                    <v-btn
                        value="fr"
                        size="small">{{ $t('common.fr') }}</v-btn>
                </v-btn-toggle>
            </div>
        </div>
    </div>
</div>
</template>
